import api from '@pray/shared/utils/api';

import type { ListenAnalyticsApiResponse, ListenAnalyticsData } from './types';

interface GetListenAnalyticsParams {
  artistId: string;
  startDate?: string;
  endDate?: string;
  // Optional per-program filter ("Stand on the Word" / "Washington Watch"). Absent = combined.
  seriesTitle?: string;
  bustCache?: boolean;
}

/**
 * Maps API response to client-friendly format
 */
function mapListenAnalytics(data: ListenAnalyticsApiResponse): ListenAnalyticsData {
  const listenByDate = data.listen_by_date ?? { historical_total: 0, daily_data: [] };

  return {
    artistName: data.artist_name,
    totalListens: data.total_listens ?? 0,
    totalUniqueListeners: data.total_unique_listeners ?? 0,
    totalListenDuration: data.total_listen_duration ?? 0,
    totalCompletedListens: data.total_completed_listens ?? 0,
    totalListenReplays: data.total_listen_replays ?? 0,
    listenByDate: {
      historicalTotal: listenByDate.historical_total ?? 0,
      dailyData: listenByDate.daily_data ?? [],
    },
    topContent: (data.top_content ?? []).map((item) => ({
      rank: item.rank,
      contentTitle: item.content_title,
      seriesTitle: item.series_title,
      totalCount: item.total_count,
      totalDuration: item.total_duration,
      totalCompleted: item.total_completed,
    })),
    listenByRegion: data.listen_by_region ?? {},
    dateRange: {
      startDate: data.date_range?.start_date ?? '',
      endDate: data.date_range?.end_date ?? '',
    },
    ageDistribution: (data.age_distribution ?? []).map((item) => ({
      label: item.age_group,
      value: item.percentage,
    })),
    genderDistribution: (data.gender_distribution ?? []).map((item) => ({
      label: item.gender,
      value: item.percentage,
    })),
    programs: (data.programs ?? []).map((p) => ({ name: p.name, count: p.count })),
    seriesTitle: data.series_title ?? null,
  };
}

/**
 * Get listen analytics for an artist
 */
export async function getListenAnalytics({
  artistId,
  startDate,
  endDate,
  seriesTitle,
  bustCache,
}: GetListenAnalyticsParams): Promise<ListenAnalyticsData> {
  if (!artistId) throw new Error('Artist ID is required');

  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (seriesTitle) params.append('seriesTitle', seriesTitle);
  if (bustCache) params.append('bustCache', 'true');

  const queryString = params.toString();
  const url = `/web/studio/artists/${artistId}/analytics/listen${queryString ? `?${queryString}` : ''}`;

  const response = await api.executeGetRequest<{ data: ListenAnalyticsApiResponse }>(url);

  return mapListenAnalytics(response.data.data);
}
